import {
  DateRangePicker,
  Label,
  SegmentedControl,
  SegmentedControlItem,
  Switch,
} from '@bill-alps/ui';
import type { DateRangeValue } from '@bill-alps/ui';
import { useLanguage, useTranslation } from '../../../i18n/useTranslation';
import { getDateLocale } from '@/lib/format/date-locale';
import type { ChartGranularity } from '../lib/time-series';

const GRANULARITIES: ChartGranularity[] = [
  'week',
  'month',
  'quarter',
  'semester',
  'year',
];

interface DashboardChartControlsProps {
  range: DateRangeValue;
  onRangeChange: (value: DateRangeValue) => void;
  granularity: ChartGranularity;
  onGranularityChange: (granularity: ChartGranularity) => void;
  cumulative: boolean;
  onCumulativeChange: (checked: boolean) => void;
  showDateRange?: boolean;
  showTimelineControls?: boolean;
}

export function DashboardChartControls({
  range,
  onRangeChange,
  granularity,
  onGranularityChange,
  cumulative,
  onCumulativeChange,
  showDateRange = true,
  showTimelineControls = true,
}: DashboardChartControlsProps) {
  const { t } = useTranslation('dashboard');
  const { language } = useLanguage();

  return (
    <div className="flex flex-wrap items-center gap-4">
      {showDateRange && (
        <DateRangePicker
          size="sm"
          value={range}
          onChange={onRangeChange}
          locale={getDateLocale(language)}
          cancelLabel={t('common:actions.cancel')}
          applyLabel={t('common:actions.apply')}
        />
      )}

      {showTimelineControls && (
        <>
          <SegmentedControl
            variant="contrast"
            layout="wrap"
            size="sm"
            value={granularity}
            onValueChange={(value) =>
              onGranularityChange(value as ChartGranularity)
            }
          >
            {GRANULARITIES.map((g) => (
              <SegmentedControlItem key={g} value={g}>
                {t(`dashboard:charts.${g}`)}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>

          <div className="flex items-center gap-2">
            <Switch checked={cumulative} onCheckedChange={onCumulativeChange} />
            <Label>{t('dashboard:charts.cumulative')}</Label>
          </div>
        </>
      )}
    </div>
  );
}
